import React, { useState, useEffect, useCallback } from 'react';
import { X, ChevronRight, ChevronLeft, Globe, Repeat, Headphones, Volume2, MessageCircle } from 'lucide-react';

const TOUR_STEPS_OP = [
  {
    target: 'waitlist-lang-selector',
    title: 'SELECT YOUR LANGUAGE',
    desc: 'Sub-Zero operates in 10 languages. Switch your comms channel here — the entire mission briefing adapts instantly. Full intel, zero delay.',
    icon: Globe,
    position: 'below',
    arrow: 'top',
  },
  {
    target: 'mode-toggle',
    title: 'SWITCH YOUR INTERFACE',
    desc: 'Toggle between Operative Mode (tactical HUD, cyberpunk) and Executive Mode (clean, corporate). Same firepower, different skin. Choose your battlefield aesthetic.',
    icon: Repeat,
    position: 'below',
    arrow: 'top',
  },
  {
    target: 'briefing-toggle',
    title: 'HEAR THE BRIEFING',
    desc: 'Select your preferred Sub-Zero agent from the roster. Each one delivers a unique AI-narrated tactical overview of the mission. Pick your voice. Get briefed.',
    icon: Headphones,
    position: 'below',
    arrow: 'top',
  },
  {
    target: 'read-to-me-toggle',
    title: 'READ TO ME',
    desc: 'Activate this and the page reads itself aloud in your selected language. Sit back, Agent — intel delivered straight to your ears while you keep your eyes on the target.',
    icon: Volume2,
    position: 'below-left',
    arrow: 'top-right',
  },
  {
    target: 'waitlist-chat-toggle',
    title: 'TALK TO A SUB-ZERO AGENT',
    desc: 'Got questions? Our AI agent is standing by 24/7. Ask anything about Sub-Zero — pricing, features, the kill switch, security protocols. Go ahead, test us.',
    icon: MessageCircle,
    position: 'above-left',
    arrow: 'bottom-right',
  },
];

const TOUR_STEPS_EX = [
  {
    target: 'waitlist-lang-selector',
    title: 'Choose Your Language',
    desc: 'Sub-Zero supports 10 languages. Select your preferred language here and all content on this page will adapt seamlessly to your choice.',
    icon: Globe,
    position: 'below',
    arrow: 'top',
  },
  {
    target: 'mode-toggle',
    title: 'Switch Interface Mode',
    desc: 'Choose between Executive Mode for a clean, professional experience or Operative Mode for a tactical, cyberpunk aesthetic. Both deliver the same powerful capabilities.',
    icon: Repeat,
    position: 'below',
    arrow: 'top',
  },
  {
    target: 'briefing-toggle',
    title: 'Listen to the Overview',
    desc: 'Hear a professionally narrated summary of the Sub-Zero platform. A curated audio overview that covers everything we offer, delivered by a voice of your choosing.',
    icon: Headphones,
    position: 'below',
    arrow: 'top',
  },
  {
    target: 'read-to-me-toggle',
    title: 'Read Aloud',
    desc: 'Have the entire page narrated in your selected language. A convenient, hands-free way to explore every feature and benefit while you focus on what matters.',
    icon: Volume2,
    position: 'below-left',
    arrow: 'top-right',
  },
  {
    target: 'waitlist-chat-toggle',
    title: 'Speak with an Advisor',
    desc: 'Have questions? Our AI-powered advisor is available around the clock. Inquire about pricing, capabilities, security, or anything else. We are happy to assist.',
    icon: MessageCircle,
    position: 'above-left',
    arrow: 'bottom-right',
  },
];

const TOUR_KEY = 'sz_tour_seen_v2';

export default function WaitlistTour({ isExec }) {
  const [step, setStep] = useState(0);
  const [visible, setVisible] = useState(false);
  const [targetRect, setTargetRect] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  const steps = isExec ? TOUR_STEPS_EX : TOUR_STEPS_OP;
  const current = steps[step];

  // Show tour after 2 seconds if not already seen
  useEffect(() => {
    const seen = localStorage.getItem(TOUR_KEY);
    if (seen) {
      setDismissed(true);
      return;
    }
    const timer = setTimeout(() => setVisible(true), 2500);
    return () => clearTimeout(timer);
  }, []);

  // Find and position tooltip relative to target
  const updatePosition = useCallback(() => {
    if (!current) return;
    const el = document.querySelector(`[data-testid="${current.target}"]`);
    if (el) {
      const rect = el.getBoundingClientRect();
      setTargetRect(rect);
      // Scroll element into view if needed
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [current]);

  useEffect(() => {
    if (visible && !dismissed) {
      updatePosition();
      const interval = setInterval(updatePosition, 500);
      return () => clearInterval(interval);
    }
  }, [visible, dismissed, step, updatePosition]);

  const next = () => {
    if (step < steps.length - 1) setStep(step + 1);
    else closeTour();
  };
  const prev = () => { if (step > 0) setStep(step - 1); };
  const closeTour = () => {
    setVisible(false);
    setDismissed(true);
    localStorage.setItem(TOUR_KEY, 'true');
  };
  const restartTour = () => {
    setStep(0);
    setDismissed(false);
    setVisible(true);
    localStorage.removeItem(TOUR_KEY);
  };

  if (dismissed && !visible) {
    // Show small "?" button to restart tour
    return (
      <button
        onClick={restartTour}
        data-testid="tour-restart-btn"
        className={`fixed bottom-6 left-6 z-[60] w-10 h-10 rounded-full flex items-center justify-center text-xs font-bold transition-all hover:scale-110 ${
          isExec
            ? 'bg-gray-900 text-white shadow-lg border border-gray-700'
            : 'bg-black/80 text-[#00FF64] border border-[#00FF64]/30 shadow-[0_0_15px_rgba(0,255,100,0.2)]'
        }`}
        title={isExec ? 'Restart guided tour' : 'Restart mission briefing'}
      >
        ?
      </button>
    );
  }

  if (!visible || !targetRect) return null;

  // Calculate tooltip position
  const getTooltipStyle = () => {
    const pad = 16;
    let top, left;

    switch (current.position) {
      case 'below':
        top = targetRect.bottom + pad;
        left = Math.max(16, Math.min(targetRect.left + targetRect.width / 2 - 160, window.innerWidth - 336));
        break;
      case 'below-left':
        top = targetRect.bottom + pad;
        left = Math.max(16, targetRect.right - 320);
        break;
      case 'above-left':
        top = targetRect.top - 220;
        left = Math.max(16, targetRect.right - 320);
        break;
      default:
        top = targetRect.bottom + pad;
        left = targetRect.left;
    }

    return { top: `${top}px`, left: `${left}px` };
  };

  // Arrow pointing to target
  const getArrowStyle = () => {
    const arrowSize = 10;
    switch (current.arrow) {
      case 'top':
        return {
          top: '-10px',
          left: `${Math.min(Math.max(targetRect.left + targetRect.width / 2 - parseInt(getTooltipStyle().left) - arrowSize, 20), 280)}px`,
          borderLeft: `${arrowSize}px solid transparent`,
          borderRight: `${arrowSize}px solid transparent`,
          borderBottom: `${arrowSize}px solid ${isExec ? '#1a1a1a' : '#00FF64'}`,
        };
      case 'top-right':
        return {
          top: '-10px',
          right: '24px',
          borderLeft: `${arrowSize}px solid transparent`,
          borderRight: `${arrowSize}px solid transparent`,
          borderBottom: `${arrowSize}px solid ${isExec ? '#1a1a1a' : '#00FF64'}`,
        };
      case 'bottom-right':
        return {
          bottom: '-10px',
          right: '24px',
          borderLeft: `${arrowSize}px solid transparent`,
          borderRight: `${arrowSize}px solid transparent`,
          borderTop: `${arrowSize}px solid ${isExec ? '#1a1a1a' : '#00FF64'}`,
        };
      default:
        return {};
    }
  };

  const Icon = current.icon;

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 z-[998]" onClick={closeTour}>
        <svg className="w-full h-full" style={{ filter: 'url(#glow)' }}>
          <defs>
            <mask id="spotlight-mask">
              <rect width="100%" height="100%" fill="white" />
              <rect
                x={targetRect.left - 6}
                y={targetRect.top - 6}
                width={targetRect.width + 12}
                height={targetRect.height + 12}
                rx="10"
                fill="black"
              />
            </mask>
            <filter id="glow">
              <feDropShadow dx="0" dy="0" stdDeviation="4" floodColor={isExec ? '#000' : '#00FF64'} floodOpacity="0.5"/>
            </filter>
          </defs>
          <rect width="100%" height="100%" fill="rgba(0,0,0,0.75)" mask="url(#spotlight-mask)" />
        </svg>
      </div>

      {/* Spotlight ring around target */}
      <div
        className="fixed z-[999] pointer-events-none rounded-xl transition-all duration-500"
        style={{
          left: targetRect.left - 6,
          top: targetRect.top - 6,
          width: targetRect.width + 12,
          height: targetRect.height + 12,
          boxShadow: isExec
            ? '0 0 0 3px rgba(26,26,26,0.8), 0 0 20px rgba(0,0,0,0.3)'
            : '0 0 0 2px #00FF64, 0 0 20px rgba(0,255,100,0.4), 0 0 40px rgba(0,255,100,0.15)',
          animation: isExec ? 'none' : 'pulse-glow 2s ease-in-out infinite',
        }}
      />

      {/* Tooltip */}
      <div
        className={`fixed z-[1000] w-[320px] rounded-xl p-0 transition-all duration-300 ${
          isExec
            ? 'bg-[#1a1a1a] border border-gray-700 shadow-2xl'
            : 'bg-[#0a0f0a] border border-[#00FF64]/30 shadow-[0_0_30px_rgba(0,255,100,0.15)]'
        }`}
        style={getTooltipStyle()}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Arrow */}
        <div className="absolute w-0 h-0" style={getArrowStyle()} />

        {/* Header */}
        <div className={`flex items-center justify-between px-4 py-3 border-b ${
          isExec ? 'border-gray-700' : 'border-[#00FF64]/15'
        }`}>
          <div className="flex items-center gap-2.5">
            <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${
              isExec ? 'bg-gray-800' : 'bg-[#00FF64]/10'
            }`}>
              <Icon className={`w-3.5 h-3.5 ${isExec ? 'text-white' : 'text-[#00FF64]'}`} />
            </div>
            <span className={`text-xs font-mono font-bold tracking-widest uppercase ${
              isExec ? 'text-white' : 'text-[#00FF64]'
            }`}>
              {current.title}
            </span>
          </div>
          <button onClick={closeTour} className={`w-6 h-6 rounded-full flex items-center justify-center transition-colors ${
            isExec ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-white/10 text-gray-500'
          }`}>
            <X className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-4 py-3">
          <p className={`text-sm leading-relaxed ${isExec ? 'text-gray-300' : 'text-gray-400'}`}>
            {current.desc}
          </p>
        </div>

        {/* Footer */}
        <div className={`flex items-center justify-between px-4 py-3 border-t ${
          isExec ? 'border-gray-700' : 'border-[#00FF64]/15'
        }`}>
          <span className={`text-[10px] font-mono ${isExec ? 'text-gray-500' : 'text-gray-600'}`}>
            {step + 1} / {steps.length}
          </span>
          <div className="flex items-center gap-2">
            {step > 0 && (
              <button onClick={prev} className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-[10px] font-mono font-bold transition-all ${
                isExec
                  ? 'text-gray-400 hover:text-white hover:bg-gray-700'
                  : 'text-gray-500 hover:text-[#00FF64] hover:bg-white/5'
              }`}>
                <ChevronLeft className="w-3 h-3" />
                {isExec ? 'BACK' : 'PREV'}
              </button>
            )}
            <button onClick={next} className={`flex items-center gap-1 px-4 py-1.5 rounded-lg text-[10px] font-mono font-bold transition-all ${
              isExec
                ? 'bg-gray-800 text-white hover:bg-gray-700 border border-gray-600'
                : 'bg-[#00FF64]/10 text-[#00FF64] hover:bg-[#00FF64]/20 border border-[#00FF64]/20'
            }`}>
              {step === steps.length - 1
                ? (isExec ? 'FINISH' : 'DEPLOY')
                : (isExec ? 'NEXT' : 'NEXT TARGET')
              }
              <ChevronRight className="w-3 h-3" />
            </button>
          </div>
        </div>

        {/* Step dots */}
        <div className="flex items-center justify-center gap-1.5 pb-3">
          {steps.map((_, i) => (
            <div
              key={i}
              className={`h-1 rounded-full transition-all ${
                i === step
                  ? `w-4 ${isExec ? 'bg-white' : 'bg-[#00FF64]'}`
                  : i < step
                    ? `w-1.5 ${isExec ? 'bg-gray-500' : 'bg-[#00FF64]/40'}`
                    : `w-1.5 ${isExec ? 'bg-gray-700' : 'bg-gray-700'}`
              }`}
            />
          ))}
        </div>
      </div>

      {/* Pulse glow animation */}
      <style>{`
        @keyframes pulse-glow {
          0%, 100% { box-shadow: 0 0 0 2px #00FF64, 0 0 20px rgba(0,255,100,0.4), 0 0 40px rgba(0,255,100,0.15); }
          50% { box-shadow: 0 0 0 3px #00FF64, 0 0 30px rgba(0,255,100,0.6), 0 0 60px rgba(0,255,100,0.25); }
        }
      `}</style>
    </>
  );
}
